import React, { useEffect, useMemo, useRef } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import useQueryFlightSelector from '@/integrations/content/content-modules/flight-selector/flight-selector.hooks';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import FlightSelectorCardList from './flight-selector-card-list/flight-selector-card-list';
import FlightSelectorSlider from './flight-selector-slider/flight-selector-slider';
import styles from './flight-selector.module.scss';

export interface FlightSelectorProps {
  /** The headline to show */
  headline: string;
  /** The UUID of the module */
  id: Content.Components['FlightSelectorModuleHeadlessDto']['id'];
  /** The text to show on the link */
  linkText: string;
  /** The UUID of the current page */
  pageId: Content.Components['FlightSelectorModuleHeadlessDto']['pageId'];
  /** The text to show as a description */
  text: string;
}

const FlightSelector = ({
  headline,
  id,
  linkText,
  pageId,
  text,
}: FlightSelectorProps) => {
  const {
    currentStep,
    flightId,
    parameters,
    setBookingUrl,
    setFlightId,
    setIsOpenTravelPlan,
    setParameters,
    setPrice,
    setStepSummaries,
    setUniqueId,
    stepSummaries,
  } = useHotelContext();
  const { bonusCodes } = useSearchContext();
  const isLaptop = useWindowSize('laptop');
  const { data: dataFlightSelector, isFetching: isFetchingFlightSelector } =
    useQueryFlightSelector({
      ...parameters,
      ...(parameters?.bonusCodes ? {} : bonusCodes ? { bonusCodes } : {}),
      id,
      pageId,
    });

  /**
   * The flight that was selected on the first load is pinned to the top of the
   * list and stays there, even after the user selects a different flight (which
   * causes a refetch where another transport is flagged as selected). The rest
   * of the list keeps the order returned by the backend.
   *
   * The pinned id is stored alongside the module identity (id + pageId) so it is
   * re-resolved if this component instance is ever reused for a different module.
   */
  const pinnedFlightRef = useRef<{
    id: number | null;
    identity: string;
    isResolved: boolean;
  } | null>(null);

  const transports = useMemo(() => {
    const items = dataFlightSelector?.transports ?? [];
    const identity = `${id}::${pageId}`;

    if (pinnedFlightRef.current?.identity !== identity) {
      pinnedFlightRef.current = { id: null, identity, isResolved: false };
    }

    if (
      !pinnedFlightRef.current.isResolved &&
      !!dataFlightSelector &&
      !isFetchingFlightSelector
    ) {
      pinnedFlightRef.current.id =
        items.find(transport => transport.isSelected)?.id ?? null;
      pinnedFlightRef.current.isResolved = true;
    }

    const pinnedId = pinnedFlightRef.current.id;
    const pinnedFlight =
      pinnedId === null
        ? undefined
        : items.find(transport => transport.id === pinnedId);

    return pinnedFlight
      ? [
          pinnedFlight,
          ...items.filter(transport => transport.id !== pinnedFlight.id),
        ]
      : items;
  }, [dataFlightSelector, id, pageId, isFetchingFlightSelector]);

  useEffect(() => {
    if (!dataFlightSelector) return;

    const transport = transports.find(transport => transport.isSelected);

    if (!transport) return;

    if (transport.id === flightId) return;

    setBookingUrl(transport.bookingUrl);
    setFlightId(transport.id);
    setParameters(transport.parameters);
    setStepSummaries(
      stepSummaries.map((stepSummary, stepSummaryIndex) =>
        stepSummaryIndex === currentStep - 1
          ? transport.description
          : stepSummary,
      ),
    );
    setPrice(transport.totalPrice);
    setUniqueId(transport.uniqueId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFlightSelector]);

  const renderFlights = () => {
    if (isFetchingFlightSelector) {
      return (
        <div className={styles.loader}>
          <SkeletonLoader
            className={styles.skeleton}
            height="88px"
            width="100%"
          />

          <SkeletonLoader
            className={styles.skeleton}
            height="88px"
            width="100%"
          />

          <SkeletonLoader
            className={styles.skeleton}
            height="88px"
            width="100%"
          />
        </div>
      );
    }

    if (!dataFlightSelector) return null;

    return (
      <React.Fragment>
        {isLaptop ? (
          <FlightSelectorCardList
            activeItem={transports.find(transport => transport.id === flightId)}
            items={transports}
            noTimeHeadline={dataFlightSelector.noTimeHeadline}
            noTimeText={dataFlightSelector.noTimeText}
            onClick={flight => {
              setBookingUrl(flight.bookingUrl);
              setFlightId(flight.id);
              setParameters(flight.parameters);
              setStepSummaries(
                stepSummaries.map((stepSummary, stepSummaryIndex) =>
                  stepSummaryIndex === currentStep - 1
                    ? flight.description
                    : stepSummary,
                ),
              );
              setPrice(flight.totalPrice);
              setUniqueId(flight.uniqueId);
            }}
          />
        ) : (
          <FlightSelectorSlider
            activeItem={transports.find(transport => transport.id === flightId)}
            items={transports}
            noTimeHeadline={dataFlightSelector.noTimeHeadline}
            noTimeText={dataFlightSelector.noTimeText}
            onClick={flight => {
              setBookingUrl(flight.bookingUrl);
              setFlightId(flight.id);
              setParameters(flight.parameters);
              setStepSummaries(
                stepSummaries.map((stepSummary, stepSummaryIndex) =>
                  stepSummaryIndex === currentStep - 1
                    ? flight.description
                    : stepSummary,
                ),
              );
              setPrice(flight.totalPrice);
              setUniqueId(flight.uniqueId);
            }}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <div className={styles.component}>
      <div className={styles.textWrapper}>
        <Typography className={styles.headline} tagStyle="bodyMediumBold">
          {headline}
        </Typography>

        <div className={styles.text}>
          <RichText text={text} />

          {linkText && (
            <AnchorLink
              className={styles.link}
              icon="ArrowRight"
              onClick={() => setIsOpenTravelPlan(true)}
              text={linkText}
            />
          )}
        </div>
      </div>

      {renderFlights()}
    </div>
  );
};

FlightSelector.displayName = 'FlightSelector';

export default FlightSelector;
